var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { useApolloClient, useMutation } from '@apollo/client';
import moment from 'moment';
import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import ADD_MANUFACTURER_PICTURE from 'api/mutations/ADD_MANUFACTURER_PICTURE';
import REMOVE_MANUFACTURER_PICTURE from 'api/mutations/REMOVE_MANUFACTURER_PICTURE';
import SET_MANUFACTURER_NON_WORKING_DAYS from 'api/mutations/SET_MANUFACTURER_NON_WORKING_DAYS';
import UPDATE_TENANT_MANUFACTURER from 'api/mutations/UPDATE_TENANT_MANUFACTURER';
import GET_MANUFACTURER_PRODUCTION_CALENDAR_DAYS from 'api/queries/GET_MANUFACTURER_PRODUCTION_CALENDAR_DAYS';
import { dataValidationErrorHandler, registerDataValidationErrorMatcher, } from 'errors/dataValidationError';
import useKeyDownListener from 'hooks/useKeyDownListener';
import NoCalendarPng from 'images/v5/empty-states/calendar.png';
import ManufacturerSvg from 'images/v5/icons/menu/manufacturers.svg';
import PlusSvg from 'images/v5/icons/plus.svg';
import TrashSvg from 'images/v5/icons/trash-03.svg';
import Button from 'layout/atoms/Button/Button';
import CalendarRange from 'layout/atoms/CalendarRange/CalendarRange';
import Dropdown from 'layout/atoms/Dropdown/Dropdown';
import Input from 'layout/atoms/Input/Input';
import Label from 'layout/atoms/Label';
import Modal from 'layout/atoms/Modal/Modal';
import Photo from 'layout/atoms/Photo/Photo';
import TableGrid, { Body, Cell, Head, Row } from 'layout/atoms/TableGrid';
import EmptyState from 'layout/molecules/EmptyState/EmptyState';
import FormField from 'layout/molecules/FormField/FormField';
import FormSaveActions from 'layout/molecules/FormSaveActions/FormSaveActions';
import Grid from 'layout/molecules/Grid/Grid';
import { closeModal, selectModalByName } from 'store/layoutSlice';
import { updateManufacturer as updateStoreManufacturer } from 'store/manufacturersSlice';
import env from 'utils/env';
import { withAttachments } from 'utils/graphql';
import toUtcDateOnlyISOString from 'utils/toUtcDateOnlyISOString';
import useManufacturerValidation from '../useManufacturerValidation';
function parseEmployeesCount(value) {
    if (value === null || value === undefined || value === '') {
        return 0;
    }
    const parsed = Number(value);
    return Number.isNaN(parsed) ? 0 : parsed;
}
function ManufacturerEditor() {
    const dispatch = useDispatch();
    const apolloClient = useApolloClient();
    const { t } = useTranslation(['specification', 'common', 'orders']);
    const modal = useSelector(selectModalByName('manufacturerEditor'));
    const manufacturerId = modal === null || modal === void 0 ? void 0 : modal.id;
    const manufacturer = useSelector((state) => state.manufacturers.items.find((item) => item.id === manufacturerId));
    const [updateManufacturer] = useMutation(UPDATE_TENANT_MANUFACTURER);
    const [setManufacturerNonWorkingDays] = useMutation(SET_MANUFACTURER_NON_WORKING_DAYS);
    const [addManufacturerPictureMutation] = useMutation(ADD_MANUFACTURER_PICTURE);
    const [removeManufacturerPictureMutation] = useMutation(REMOVE_MANUFACTURER_PICTURE);
    const addManufacturerPicture = withAttachments(addManufacturerPictureMutation);
    const [manufacturerForm, setManufacturerForm] = useState({
        fullName: '',
        shortName: '',
        vatNumber: '',
        countryOfOrigin: '',
        addressPostalCode: '',
        addressCity: '',
        addressStreet: '',
        addressNumber: '',
        emailAddress: '',
        contactPhone: '',
        employeesCount: '',
    });
    const [calendarRange, setCalendarRange] = useState({
        startDate: null,
        endDate: null,
    });
    const [nonWorkingDays, setNonWorkingDays] = useState([]);
    const [occupiedOrderIdsByDate, setOccupiedOrderIdsByDate] = useState({});
    const [occupiedOrdersByDate, setOccupiedOrdersByDate] = useState({});
    const [pictureFile, setPictureFile] = useState(null);
    const [picturePreview, setPicturePreview] = useState(null);
    const [removePicture, setRemovePicture] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [error] = useState('');
    const latestCalendarRequestRef = useRef('');
    useEffect(() => {
        var _a;
        if (!manufacturer) {
            return;
        }
        setManufacturerForm({
            fullName: manufacturer.fullName || '',
            shortName: manufacturer.shortName || '',
            vatNumber: manufacturer.vatNumber || '',
            countryOfOrigin: manufacturer.countryOfOrigin || '',
            addressPostalCode: manufacturer.addressPostalCode || '',
            addressCity: manufacturer.addressCity || '',
            addressStreet: manufacturer.addressStreet || '',
            addressNumber: manufacturer.addressNumber || '',
            emailAddress: manufacturer.emailAddress || '',
            contactPhone: manufacturer.contactPhone || '',
            employeesCount: manufacturer.employeesCount !== null && manufacturer.employeesCount !== undefined
                ? String(manufacturer.employeesCount)
                : '',
        });
        setNonWorkingDays(((_a = manufacturer.nonWorkingDays) === null || _a === void 0 ? void 0 : _a.map((range) => ({
            startDate: range.startDate ? moment(range.startDate) : null,
            endDate: range.endDate ? moment(range.endDate) : null,
        }))) || []);
        setCalendarRange({ startDate: null, endDate: null });
        setPictureFile(null);
        setPicturePreview(null);
        setRemovePicture(false);
    }, [manufacturer]);
    useEffect(() => {
        latestCalendarRequestRef.current = '';
        setOccupiedOrderIdsByDate({});
        setOccupiedOrdersByDate({});
    }, [manufacturer === null || manufacturer === void 0 ? void 0 : manufacturer.id]);
    const { errors, isValid, pendingFields, markTouched, validateAll } = useManufacturerValidation(manufacturerForm, t, 'fullName');
    const handleClose = () => {
        dispatch(closeModal({ name: 'manufacturerEditor' }));
    };
    const handleVariantChange = ({ target: { name, value }, }) => {
        setManufacturerForm((mfr) => (Object.assign(Object.assign({}, mfr), { [name]: value })));
    };
    const handlePictureAdd = useCallback((chosenFiles) => {
        const files = Array.isArray(chosenFiles) ? chosenFiles : [chosenFiles];
        if (files.length > 0) {
            const file = files[0];
            setPictureFile(file);
            setRemovePicture(false);
            const reader = new FileReader();
            reader.onloadend = () => {
                setPicturePreview(reader.result);
            };
            reader.readAsDataURL(file);
        }
    }, []);
    const handlePictureDelete = () => {
        setPictureFile(null);
        setPicturePreview(null);
        setRemovePicture(true);
    };
    const disabledDates = useMemo(() => {
        const dates = [];
        nonWorkingDays.forEach((range) => {
            if (!range.startDate || !range.endDate) {
                return;
            }
            const cursor = range.startDate.clone().startOf('day');
            const end = range.endDate.clone().startOf('day');
            while (cursor.isSameOrBefore(end, 'day')) {
                dates.push(cursor.clone());
                cursor.add(1, 'day');
            }
        });
        return dates;
    }, [nonWorkingDays]);
    const occupiedDates = useMemo(() => { var _a; return ((_a = manufacturer === null || manufacturer === void 0 ? void 0 : manufacturer.occupiedProductionDates) === null || _a === void 0 ? void 0 : _a.map((date) => moment(date).startOf('day'))) || []; }, [manufacturer === null || manufacturer === void 0 ? void 0 : manufacturer.occupiedProductionDates]);
    const minDate = useMemo(() => moment().startOf('day'), []);
    const hasBlockedDatesInRange = useCallback((startDate, endDate) => {
        const blockedDates = [...disabledDates, ...occupiedDates];
        const cursor = startDate.clone().startOf('day');
        const end = endDate.clone().startOf('day');
        while (cursor.isSameOrBefore(end, 'day')) {
            if (blockedDates.some((day) => day.isSame(cursor, 'day'))) {
                return true;
            }
            cursor.add(1, 'day');
        }
        return false;
    }, [disabledDates, occupiedDates]);
    const selectedRangeHasBlockedDates = useMemo(() => {
        if (!calendarRange.startDate) {
            return false;
        }
        const selectedEndDate = calendarRange.endDate || calendarRange.startDate;
        return hasBlockedDatesInRange(calendarRange.startDate, selectedEndDate);
    }, [calendarRange.endDate, calendarRange.startDate, hasBlockedDatesInRange]);
    const handleCalendarChange = (startDate, endDate) => {
        setCalendarRange({ startDate, endDate });
    };
    const handleCalendarVisibleRangeChange = useCallback((visibleStartDate, visibleEndDate) => __awaiter(this, void 0, void 0, function* () {
        var _a, _b;
        if (!(manufacturer === null || manufacturer === void 0 ? void 0 : manufacturer.id)) {
            setOccupiedOrderIdsByDate({});
            setOccupiedOrdersByDate({});
            return;
        }
        const startDate = toUtcDateOnlyISOString(visibleStartDate);
        const endDate = toUtcDateOnlyISOString(visibleEndDate);
        if (!startDate || !endDate) {
            setOccupiedOrderIdsByDate({});
            setOccupiedOrdersByDate({});
            return;
        }
        const requestKey = `${manufacturer.id}:${startDate}:${endDate}`;
        latestCalendarRequestRef.current = requestKey;
        try {
            const response = yield apolloClient.query({
                query: GET_MANUFACTURER_PRODUCTION_CALENDAR_DAYS,
                variables: {
                    manufacturerId: manufacturer.id,
                    startDate,
                    endDate,
                },
                fetchPolicy: 'network-only',
            });
            if (latestCalendarRequestRef.current !== requestKey) {
                return;
            }
            const calendarDays = ((_b = (_a = response.data) === null || _a === void 0 ? void 0 : _a.node) === null || _b === void 0 ? void 0 : _b.productionCalendarDays) || [];
            const occupiedOrderIdsByDateMap = Object.fromEntries(calendarDays.map((day) => [
                moment(day.date).format('YYYY-MM-DD'),
                day.orderIds || [],
            ]));
            const occupiedOrdersByDateMap = Object.fromEntries(calendarDays.map((day) => [
                moment(day.date).format('YYYY-MM-DD'),
                (day.orders || []).map((order) => ({
                    orderId: order === null || order === void 0 ? void 0 : order.orderId,
                    specificationNames: (order === null || order === void 0 ? void 0 : order.specificationNames) || [],
                })),
            ]));
            setOccupiedOrderIdsByDate(occupiedOrderIdsByDateMap);
            setOccupiedOrdersByDate(occupiedOrdersByDateMap);
        }
        catch (_c) {
            if (latestCalendarRequestRef.current === requestKey) {
                setOccupiedOrderIdsByDate({});
                setOccupiedOrdersByDate({});
            }
        }
    }), [apolloClient, manufacturer === null || manufacturer === void 0 ? void 0 : manufacturer.id]);
    const handleAddNonWorkingDay = () => {
        if (!calendarRange.startDate) {
            return;
        }
        const startDate = calendarRange.startDate.clone().startOf('day');
        const endDate = calendarRange.endDate
            ? calendarRange.endDate.clone().startOf('day')
            : startDate.clone();
        if (endDate < startDate || hasBlockedDatesInRange(startDate, endDate)) {
            return;
        }
        setNonWorkingDays((ranges) => [...ranges, { startDate, endDate }]);
        setCalendarRange({ startDate: null, endDate: null });
    };
    const handleRemoveNonWorkingDay = (index) => {
        setNonWorkingDays((ranges) => ranges.filter((_, idx) => idx !== index));
    };
    const formatRangeLabel = (range) => {
        if (!range.startDate || !range.endDate) {
            return '';
        }
        const startLabel = range.startDate.format('DD.MM.YYYY');
        const endLabel = range.endDate.format('DD.MM.YYYY');
        return startLabel === endLabel ? startLabel : `${startLabel} - ${endLabel}`;
    };
    const handleVariantUpdate = () => __awaiter(this, void 0, void 0, function* () {
        var _a, _b;
        if (!validateAll() || !manufacturer)
            return;
        const handleError = dataValidationErrorHandler(dispatch);
        setIsLoading(true);
        try {
            const updateResponse = yield updateManufacturer({
                variables: {
                    manufacturerId: manufacturer.id,
                    fullName: manufacturerForm.fullName,
                    shortName: manufacturerForm.shortName,
                    vatNumber: manufacturerForm.vatNumber,
                    countryOfOrigin: manufacturerForm.countryOfOrigin,
                    addressPostalCode: manufacturerForm.addressPostalCode,
                    addressCity: manufacturerForm.addressCity,
                    addressStreet: manufacturerForm.addressStreet,
                    addressNumber: manufacturerForm.addressNumber,
                    emailAddress: manufacturerForm.emailAddress,
                    contactPhone: manufacturerForm.contactPhone,
                    employeesCount: parseEmployeesCount(manufacturerForm.employeesCount),
                },
            });
            const nonWorkingDaysPayload = nonWorkingDays
                .filter((range) => range.startDate && range.endDate)
                .map((range) => ({
                startDate: range.startDate.clone().startOf('day').toISOString(),
                endDate: range.endDate.clone().startOf('day').toISOString(),
            }));
            let finalResponse = updateResponse;
            if (pictureFile) {
                const pictureResponse = yield addManufacturerPicture({
                    variables: { manufacturerId: manufacturer.id, file: pictureFile },
                });
                finalResponse = Object.assign(Object.assign({}, updateResponse), { data: Object.assign(Object.assign({}, updateResponse.data), { updateManufacturer: Object.assign(Object.assign({}, updateResponse.data.updateManufacturer), { manufacturer: pictureResponse.data.addManufacturerPicture.manufacturer }) }) });
            }
            else if (removePicture && manufacturer.picture) {
                const pictureResponse = yield removeManufacturerPictureMutation({
                    variables: { manufacturerId: manufacturer.id },
                });
                finalResponse = Object.assign(Object.assign({}, updateResponse), { data: Object.assign(Object.assign({}, updateResponse.data), { updateManufacturer: Object.assign(Object.assign({}, updateResponse.data.updateManufacturer), { manufacturer: (_a = pictureResponse.data) === null || _a === void 0 ? void 0 : _a.removeManufacturerPicture.manufacturer }) }) });
            }
            if (manufacturerId) {
                const nonWorkingDaysResponse = yield setManufacturerNonWorkingDays({
                    variables: { manufacturerId, nonWorkingDays: nonWorkingDaysPayload },
                });
                finalResponse = Object.assign(Object.assign({}, finalResponse), { data: Object.assign(Object.assign({}, finalResponse.data), { updateManufacturer: Object.assign(Object.assign({}, finalResponse.data.updateManufacturer), { manufacturer: (_b = nonWorkingDaysResponse.data) === null || _b === void 0 ? void 0 : _b.setManufacturerNonWorkingDays }) }) });
            }
            dispatch(updateStoreManufacturer(finalResponse));
            dispatch(closeModal({ name: 'manufacturerEditor' }));
        }
        catch (updateError) {
            setIsLoading(false);
            handleError(updateError);
        }
    });
    registerDataValidationErrorMatcher();
    useKeyDownListener([
        { code: 'Enter', callback: handleVariantUpdate },
        { code: 'Escape', callback: handleClose },
    ]);
    const existingPicture = (manufacturer === null || manufacturer === void 0 ? void 0 : manufacturer.picture) && !removePicture ? `${env.static}/${manufacturer.picture}` : null;
    const pictureSrc = typeof picturePreview === 'string' ? picturePreview : existingPicture;
    return (React.createElement(Modal, { name: "manufacturerEditor", size: "m", header: React.createElement(Label, { weight: "strong" },
            React.createElement(ManufacturerSvg, null),
            " ",
            t('specification:edit_manufacturer')), footer: React.createElement(FormSaveActions, { isValid: isValid, isLoading: isLoading, pendingFields: pendingFields, onSave: handleVariantUpdate, onCancel: handleClose, validateAll: validateAll, disabled: !manufacturer }) },
        React.createElement(Grid, { gap: 32 },
            React.createElement(Grid, null,
                React.createElement(Label, null, t('common:MANUFACTURER_AVATAR')),
                React.createElement(Photo, { theme: "avatar", size: "l", src: pictureSrc, alt: manufacturerForm.fullName || 'Manufacturer picture', onPhotoAdd: handlePictureAdd, onPhotoDelete: pictureSrc ? handlePictureDelete : undefined })),
            React.createElement(Grid, null,
                React.createElement(Label, null, t('common:MANUFACTURER_BASIC_INFO')),
                React.createElement(FormField, { direction: "row", label: t('manufacturer_name_capitalize'), error: errors.fullName },
                    React.createElement(Input, { value: manufacturerForm.fullName, onChange: handleVariantChange, onBlur: () => markTouched('fullName'), name: "fullName", error: Boolean(errors.fullName), disabled: !(manufacturer === null || manufacturer === void 0 ? void 0 : manufacturer.canFullNameBeUpdated) })),
                React.createElement(FormField, { direction: "row", label: t('manufacturer_shortname'), error: errors.shortName },
                    React.createElement(Input, { value: manufacturerForm.shortName, onChange: handleVariantChange, onBlur: () => markTouched('shortName'), name: "shortName", error: Boolean(errors.shortName) })),
                React.createElement(FormField, { direction: "row", label: t('specification:vat_number') },
                    React.createElement(Input, { value: manufacturerForm.vatNumber, onChange: handleVariantChange, name: "vatNumber" }))),
            React.createElement(Grid, null,
                React.createElement(Label, null, t('common:MANUFACTURER_ADDRESS')),
                React.createElement(FormField, { direction: "row", label: t('specification:country_of_origin') },
                    React.createElement(Input, { value: manufacturerForm.countryOfOrigin, onChange: handleVariantChange, name: "countryOfOrigin" })),
                React.createElement(FormField, { direction: "row", label: t('specification:address_postal_code') },
                    React.createElement(Input, { value: manufacturerForm.addressPostalCode, onChange: handleVariantChange, name: "addressPostalCode" })),
                React.createElement(FormField, { direction: "row", label: t('specification:address_city') },
                    React.createElement(Input, { value: manufacturerForm.addressCity, onChange: handleVariantChange, name: "addressCity" })),
                React.createElement(FormField, { direction: "row", label: t('specification:address_street') },
                    React.createElement(Input, { value: manufacturerForm.addressStreet, onChange: handleVariantChange, name: "addressStreet" })),
                React.createElement(FormField, { direction: "row", label: t('specification:address_number') },
                    React.createElement(Input, { value: manufacturerForm.addressNumber, onChange: handleVariantChange, name: "addressNumber" }))),
            React.createElement(Grid, null,
                React.createElement(Label, null, t('common:MANUFACTURER_CONTACT')),
                React.createElement(FormField, { direction: "row", label: t('specification:email_address') },
                    React.createElement(Input, { value: manufacturerForm.emailAddress, onChange: handleVariantChange, name: "emailAddress" })),
                React.createElement(FormField, { direction: "row", label: t('specification:contact_phone') },
                    React.createElement(Input, { value: manufacturerForm.contactPhone, onChange: handleVariantChange, name: "contactPhone" }))),
            React.createElement(Grid, null,
                React.createElement(Label, null, t('common:MANUFACTURER_DETAILS')),
                React.createElement(FormField, { direction: "row", label: t('specification:employees_count') },
                    React.createElement(Input, { value: manufacturerForm.employeesCount, onChange: handleVariantChange, name: "employeesCount", type: "number" })),
                React.createElement(TableGrid, { columns: [
                        ['200px', '1fr'],
                        ['72px', 'auto'],
                    ] },
                    React.createElement(Head, null,
                        React.createElement(Row, { head: true },
                            React.createElement(Cell, null, t('specification:non_working_days')),
                            React.createElement(Cell, { align: "right" },
                                React.createElement(Dropdown, { closeOnClick: false, trigger: React.createElement(Button, { size: "xs", theme: "ghost", shape: "rounded", leftIcon: React.createElement(PlusSvg, null) }) },
                                    React.createElement(CalendarRange, { startDate: calendarRange.startDate, endDate: calendarRange.endDate, onDateChange: handleCalendarChange, onVisibleRangeChange: handleCalendarVisibleRangeChange, disabledDates: disabledDates, occupiedDates: occupiedDates, occupiedDateOrderIds: occupiedOrderIdsByDate, occupiedDateOrders: occupiedOrdersByDate, legend: {
                                            nonWorking: t('orders:planning_calendar_non_working_legend'),
                                            occupied: t('orders:planning_calendar_occupied_legend'),
                                        }, minDate: minDate }),
                                    React.createElement(Button, { color: "primary", onClick: handleAddNonWorkingDay, disabled: !calendarRange.startDate || selectedRangeHasBlockedDates }, t('specification:add_non_working_days')))))),
                    nonWorkingDays.length > 0 ? (React.createElement(Body, null, nonWorkingDays.map((range, index) => {
                        var _a, _b;
                        return (React.createElement(Row, { key: `${(_a = range.startDate) === null || _a === void 0 ? void 0 : _a.format('YYYY-MM-DD')}-${(_b = range.endDate) === null || _b === void 0 ? void 0 : _b.format('YYYY-MM-DD')}` },
                            React.createElement(Cell, null,
                                React.createElement("span", null, formatRangeLabel(range))),
                            React.createElement(Cell, { align: "right" },
                                React.createElement(Button, { size: "xs", theme: "ghost", color: "danger", shape: "rounded", leftIcon: React.createElement(TrashSvg, null), onClick: () => handleRemoveNonWorkingDay(index) }))));
                    }))) : (React.createElement(Body, null,
                        React.createElement(Row, null,
                            React.createElement(Cell, { span: 2, align: "right" },
                                React.createElement(EmptyState, { size: "s", icon: React.createElement("img", { src: NoCalendarPng, alt: "" }), header: t('specification:WORKING_DAYS_EMPTY_HEADER'), noSubheader: true })))))))),
        error && React.createElement("h5", { className: "errorText" }, error)));
}
export default memo(ManufacturerEditor);
